import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { subscribeEnergy, subscribeEnergyKick } from '@/lib/energyBus'

/**
 * PlumeField — a feathered waveform of gradient plumes rooted behind an input,
 * driven by the energyBus (keystrokes today, voice amplitude tomorrow).
 *
 * Locked design values from the plume study (rev 11):
 * - The field idles at half presence (PRESENCE) and typing sweeps the headroom.
 * - One master breath rolls across the field; per-plume oscillators add a
 *   visible second voice; keystroke kicks land on damped-spring velocities
 *   so they bloom (~150ms) and settle (~600ms) instead of jerking.
 * - Palette/blur/feather live in index.css (.plume-* classes, light + dark).
 *
 * The caller positions the field via className (absolute box inside a
 * relative, usually overflow-hidden parent).
 */

const PRESENCE = 0.5 // baked-in idle presence; raw energy 0 = this look
const SPRING_W = 7.5 // rad/s — keystroke swell spring
const SPRING_Z = 0.9 // near-critical damping: a swell, not a wobble

interface PlumeDef {
  x: number
  w: number
  gain: number
  k: number
  tilt: number
  shape: 's1' | 's2' | 's3'
  mix: 'hot' | 'violet' | 'ember'
  oscSpeed: number
  xSpeed: number
  ph: number
}

const PLUMES: PlumeDef[] = [
  {
    x: -4,
    w: 30,
    gain: 0.62,
    k: 0.055,
    tilt: -3,
    shape: 's2',
    mix: 'violet',
    oscSpeed: 0.62,
    xSpeed: 0.21,
    ph: 0.0,
  },
  {
    x: 12,
    w: 26,
    gain: 0.9,
    k: 0.035,
    tilt: 2,
    shape: 's1',
    mix: 'hot',
    oscSpeed: 0.5,
    xSpeed: 0.16,
    ph: 2.1,
  },
  {
    x: 26,
    w: 36,
    gain: 0.5,
    k: 0.07,
    tilt: -1,
    shape: 's3',
    mix: 'ember',
    oscSpeed: 0.72,
    xSpeed: 0.26,
    ph: 4.0,
  },
  {
    x: 44,
    w: 28,
    gain: 1.0,
    k: 0.03,
    tilt: 3,
    shape: 's2',
    mix: 'hot',
    oscSpeed: 0.44,
    xSpeed: 0.19,
    ph: 1.2,
  },
  {
    x: 58,
    w: 34,
    gain: 0.66,
    k: 0.06,
    tilt: -2,
    shape: 's1',
    mix: 'violet',
    oscSpeed: 0.66,
    xSpeed: 0.23,
    ph: 5.3,
  },
  {
    x: 74,
    w: 26,
    gain: 0.86,
    k: 0.04,
    tilt: 1,
    shape: 's3',
    mix: 'ember',
    oscSpeed: 0.56,
    xSpeed: 0.15,
    ph: 3.3,
  },
  {
    x: 86,
    w: 30,
    gain: 0.58,
    k: 0.065,
    tilt: -3,
    shape: 's2',
    mix: 'violet',
    oscSpeed: 0.7,
    xSpeed: 0.24,
    ph: 0.7,
  },
]

function knee(x: number) {
  return Math.tanh(x * 1.35) / Math.tanh(1.35)
}

export function PlumeField({
  className,
  tempo = 1,
  driftScale = 1,
  presence = PRESENCE,
  plumeScale = 1,
}: {
  className?: string
  /** Time multiplier for breath/oscillation — large full-width fields read better slower (~0.6). */
  tempo?: number
  /** Multiplier on lateral ping-pong travel — scale up for wide fields. */
  driftScale?: number
  /** Baked-in idle presence (0..1). Lower it for fields that should sit low and grow visibly with energy. */
  presence?: number
  /** Multiplier on plume widths — bigger plumes for big screens. */
  plumeScale?: number
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const tuningRef = useRef({ tempo, driftScale, presence })
  tuningRef.current = { tempo, driftScale, presence }

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const bed = host.querySelector<HTMLElement>('[data-plume-bed]')
    const els = Array.from(host.querySelectorAll<HTMLElement>('[data-plume]'))
    const state = PLUMES.map((d, i) => ({
      ...d,
      el: els[i]!,
      level: 0,
      theta: (d.ph * 7919) % 6.28, // deterministic spread; Math.random breaks nothing here but this keeps SSR-safe habits
      thetaX: (d.ph * 104729) % 6.28,
      impY: 0,
      impX: 0,
      impR: 0,
      velY: 0,
      velX: 0,
      velR: 0,
    }))

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let level = 0
    let breathPhase = 0
    let last = performance.now()
    let rafId: number

    const unsubLevel = subscribeEnergy((l) => {
      level = l
    })
    const unsubKick = subscribeEnergyKick((l) => {
      const pr = tuningRef.current.presence
      const kick = 0.35 + knee(pr + l * (1 - pr)) * 0.65
      for (const p of state) {
        p.velY += (Math.random() - 0.5) * 1.5 * kick
        p.velX += (Math.random() - 0.5) * 54 * kick
        p.velR += (Math.random() - 0.5) * 15 * kick
      }
    })

    const isLight = () => !document.documentElement.classList.contains('dark')

    const frame = (now: number) => {
      const dt = Math.min(50, now - last)
      last = now
      const dts = dt / 1000

      const { tempo: tempoNow, driftScale: driftNow, presence: presenceNow } = tuningRef.current
      const e = knee(presenceNow + level * (1 - presenceNow))
      // idle runs at half speed; live energy ramps back to full tempo
      const speedScale = (0.5 + 0.5 * level) * tempoNow

      // breathing under exertion: a touch faster and deeper, never panting
      breathPhase += dts * 6.28 * (0.24 + e * 0.12) * speedScale
      const breathAmp = reduceMotion ? 0 : 0.03 + e * 0.04

      for (const p of state) {
        p.level += (e * p.gain - p.level) * p.k * (dt / 16.7)

        p.theta += dts * p.oscSpeed * 6.28 * (1.05 + e * 0.45) * speedScale
        p.thetaX += dts * p.xSpeed * 6.28 * (1.0 + e * 0.4) * speedScale

        // keystroke swells: velocity kicks through a damped spring
        p.velY += (-SPRING_W * SPRING_W * p.impY - 2 * SPRING_Z * SPRING_W * p.velY) * dts
        p.impY += p.velY * dts
        p.velX += (-SPRING_W * SPRING_W * p.impX - 2 * SPRING_Z * SPRING_W * p.velX) * dts
        p.impX += p.velX * dts
        p.velR += (-SPRING_W * SPRING_W * p.impR - 2 * SPRING_Z * SPRING_W * p.velR) * dts
        p.impR += p.velR * dts

        // one shared breath rolls across the field; per-plume voice on top
        const breath = breathAmp * Math.sin(breathPhase - (p.x / 100) * 2.4 + p.ph * 0.3)
        const micro = reduceMotion ? 0 : (0.022 + e * 0.075) * Math.sin(p.theta + p.ph)
        const xDrift = reduceMotion ? 0 : (9 + e * 16) * driftNow * Math.sin(p.thetaX + p.ph)

        const s = Math.max(0.08, 0.26 + p.level * 0.58 + breath + micro + p.impY)
        const rot =
          p.tilt + p.impR + (reduceMotion ? 0 : Math.sin(p.theta * 0.5 + p.ph) * (0.5 + e * 0.9))

        p.el.style.transform =
          'translateX(' +
          (xDrift + p.impX).toFixed(2) +
          'px) rotate(' +
          rot.toFixed(2) +
          'deg) scaleY(' +
          Math.min(1.0, s).toFixed(4) +
          ')'
        const lightBoost = isLight() ? 0.18 : 0
        p.el.style.opacity = (0.42 + lightBoost + Math.min(0.28, p.level * 0.45)).toFixed(3)
      }

      const fieldBreath = reduceMotion ? 0 : 0.03 * Math.sin(breathPhase)
      host.style.opacity = (0.45 + e * 0.35 + fieldBreath).toFixed(3)
      if (bed) bed.style.opacity = (0.15 + e * 0.55 + fieldBreath).toFixed(3)

      rafId = requestAnimationFrame(frame)
    }
    rafId = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(rafId)
      unsubLevel()
      unsubKick()
    }
  }, [])

  return (
    <div
      ref={hostRef}
      aria-hidden
      className={cn('plume-field pointer-events-none absolute', className)}>
      <div data-plume-bed className="plume-bed" />
      {PLUMES.map((p) => (
        <div
          key={p.ph}
          data-plume
          className={`plume plume-${p.shape} plume-${p.mix}`}
          style={{
            left: `${p.x - (p.w * (plumeScale - 1)) / 2}%`,
            width: `${p.w * plumeScale}%`,
          }}
        />
      ))}
    </div>
  )
}

/**
 * PlumeAura — the feathered, uneven glow that hugs a focused input in place
 * of a hard focus ring. Brightens with live energy while typing.
 */
export function PlumeAura({ focused, className }: { focused: boolean; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const focusedRef = useRef(focused)
  focusedRef.current = focused

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const apply = (level: number) => {
      el.style.opacity = Math.min(0.7, (focusedRef.current ? 0.28 : 0) + level * 0.42).toFixed(3)
    }
    apply(0)
    return subscribeEnergy(apply)
  }, [])

  // re-apply immediately when focus flips (bus may be idle)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const current = parseFloat(el.style.opacity || '0')
    const energyPart = Math.max(0, current - (focused ? 0 : 0.28))
    el.style.opacity = Math.min(0.7, (focused ? 0.28 : 0) + energyPart).toFixed(3)
  }, [focused])

  return <div ref={ref} aria-hidden className={cn('plume-aura', className)} />
}
